import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IDateNavigationService, IGroupService, IlastProcessingResultsService, IOnlineComputationService } from '../../../services';
import { ComponentDefinition, LastProcessedDisplay, LastProcessedDisplayByGroup, LastProcessingResult, RefreshClickData, ResultsStatus } from '../../../models';
import { refreshClick, updateSchedulingIcons } from '../../../common';

class TimelineDetailsStateController implements angular.IComponentController {
    beginDate: string;
    endDate: string;
    groupGuidList: string[];
    groups: any[];
    initialRange: { beginDate: string; endDate: string };
    isLoading: boolean;
    lastProcessedDisplay: LastProcessedDisplay[];
    lastProcessedDisplayByGroup: LastProcessedDisplayByGroup;
    loadingMessage: string;
    worstStatusByGroup: { [groupGuid: string]: ResultsStatus } = {};

    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$stateParams',
        '$q',
        'lastProcessingResultsService',
        'dateNavigationService',
        'groupService',
        'onlineComputationService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _$q: angular.IQService,
        private _lastProcessingResultsService: IlastProcessingResultsService,
        private _dateNavigationService: IDateNavigationService,
        private _groupService: IGroupService,
        private _onlineComputationService: IOnlineComputationService
    ) {
    }

    $onInit() {
        this.lastProcessedDisplay = [];
        this.lastProcessedDisplayByGroup = {};
        if (this._$stateParams.beginDate) {
            this.beginDate = moment(this._$stateParams.beginDate).toISOString();
        }
        if (this._$stateParams.endDate) {
            this.endDate = moment(this._$stateParams.endDate).toISOString();
        }
        if (!this.beginDate && !this.endDate) {
            const lastResultsRange = this._dateNavigationService.getLastNPeriod(100);
            this.beginDate = lastResultsRange.beginDate.toISOString();
            this.endDate = lastResultsRange.endDate.toISOString();
        }
        this.initialRange = {
            beginDate: this.beginDate,
            endDate: this.endDate
        };
        // Load groups (in tree format) and retrieve last processed data (global and by group)
        this.isLoading = true;
        this._$q.all([this.refreshGsvm(), this.refreshLastProcessed(this.beginDate, this.endDate)])
            .finally(() => {
                this._garbages.push(this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
                    this.isLoading = true;
                    return this.refreshLastProcessed(data.beginDate, data.endDate)
                        .finally(() => {
                            this.updateGroups(this.groups, this.worstStatusByGroup);
                            this.isLoading = false;
                        });
                }));
                this.updateGroups(this.groups, this.worstStatusByGroup);
                this.isLoading = false;
            });
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    refreshGsvm(): angular.IPromise<void> {
        return this._onlineComputationService
            .getSchedulingViewModels()
            .then(data => {
                this.groups = this._groupService.convertToTree(data.list as any[]);
            });
    }

    private refreshLastProcessed(beginDate: string, endDate: string) {
        if (beginDate && endDate) {
            const promises = [
                this._lastProcessingResultsService.getLastProcessingResults(beginDate, endDate, true),
                this._lastProcessingResultsService.getLastProcessingResultsByGroup(beginDate, endDate, true)
            ];
            return this._$q.all(promises)
                .then((responses) => {
                    this.lastProcessedDisplay = [];
                    this.lastProcessedDisplayByGroup = {};
                    // For main timeline
                    const globalResponse = responses[0] as any;
                    // For details
                    const byGroupResponse = responses[1] as any;
                    // Map global results for display
                    this.lastProcessedDisplay = globalResponse.map((d: LastProcessingResult) => {
                        return {
                            beginDate: new Date(d.beginDate),
                            endDate: new Date(d.endDate),
                            classed: ResultsStatus[d.status]?.toLocaleLowerCase(),
                            message: `${d.beginDate} - ${d.endDate}: ${ResultsStatus[d.status]}`
                        };
                    });
                    // Map groups results for display
                    this.groupGuidList = Object.keys(byGroupResponse);
                    this.worstStatusByGroup = {};
                    this.groupGuidList.forEach(key => {
                        this.lastProcessedDisplayByGroup[key] = byGroupResponse[key].map((d: LastProcessingResult) => {
                            if ((!this.worstStatusByGroup[key] && d.status !== ResultsStatus.Unknown) || (d.status !== ResultsStatus.Unknown && d.status > this.worstStatusByGroup[key])) {
                                // Exception for end date
                                if (d.endDate !== d.beginDate) {
                                    this.worstStatusByGroup[key] = d.status;
                                }
                            }
                            return {
                                beginDate: new Date(d.beginDate),
                                endDate: new Date(d.endDate),
                                classed: ResultsStatus[d.status]?.toLocaleLowerCase(),
                                message: `${d.beginDate} - ${d.endDate}: ${ResultsStatus[d.status]}`
                            };
                        });
                    });
                    if (this.groups) {
                        this.groups.forEach(group => {
                            if (this.groupGuidList.indexOf(group.guid) < 0) {
                                this.lastProcessedDisplayByGroup[group.guid] = [];
                                this.worstStatusByGroup[group.guid] = ResultsStatus.Unknown;
                            }
                        });
                    }
                });
        } else {
            return this._$q.when();
        }
    }

    private updateGroups(groups: any[], worstStatusByGroup: { [groupGuid: string]: ResultsStatus }): any[] {
        if (groups && groups.length > 0 && worstStatusByGroup) {
            groups = groups.map(g => {
                g.severity = ResultsStatus[worstStatusByGroup[g.guid]]?.toLocaleLowerCase();
                if (!g.severity) {
                    g.severity = 'none';
                }
                if (g.statusesByStep && g.severity) {
                    const keys = Object.keys(g.statusesByStep);
                    keys.forEach(key => {
                        if (g.statusesByStep.hasOwnProperty(key)) {
                            if (g.severity === 'none') {
                                g.statusesByStep[key] = ResultsStatus.Unknown;
                            } else {
                                g.statusesByStep[key] = worstStatusByGroup[g.guid];
                            }
                        }
                    });
                }
                this._$rootScope.$broadcast(updateSchedulingIcons, g);
                return g;
            });
        }
        return groups;
    }
}

export const wtTimelineDetailsState: ComponentDefinition = {
    componentId: 'wtTimelineDetailsState',
    controller: TimelineDetailsStateController,
    template: require('./timelineDetailsState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};
